import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minimize2,
  Minus,
  Monitor,
  MoveHorizontal,
  Plus,
  Shirt,
  Sparkles,
  User,
  UserRound,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/liposuction-surgery-mumbai";

const LIPOSUCTION_FAQS = [
  {
    question: "Is liposuction a weight loss procedure?",
    answer:
      "No. Liposuction removes localized fat deposits for body contouring, not overall weight reduction. The amount of fat removed in a single safe session, typically 2 to 5 liters, produces a change in body shape and proportion but not a clinically significant change in body weight. Patients who need to lose weight for health reasons should do so before considering liposuction for contouring.",
  },
  {
    question: "Are liposuction results permanent?",
    answer:
      "The fat cells removed during liposuction are gone permanently. They do not regenerate. However, the remaining fat cells in the treated area can still enlarge with significant weight gain. Patients who maintain a stable weight after liposuction preserve their result long-term. Significant weight gain after surgery causes fat to redistribute to whatever cells remain, which can be in treated and untreated areas.",
  },
  {
    question: "Will skin sag after liposuction?",
    answer:
      "This depends on skin elasticity. Patients with good elasticity experience skin contraction after fat removal and the result is smooth. Patients with reduced elasticity, from age, prior weight loss, or pregnancy, may develop some laxity after fat removal. Dr. Jain assesses elasticity before surgery and advises on whether liposuction alone produces the intended result or whether a skin-removing procedure alongside it is needed.",
  },
  {
    question: "How many areas can be treated in one session?",
    answer:
      "Multiple areas can be treated in a single procedure. The limiting factor is the total volume of fat safely removed in one session, approximately 2 to 5 liters, and the total surgical time and anesthesia exposure. Dr. Jain plans multi-area treatments within safe limits during consultation. Patients who need more extensive contouring than a single session allows are advised on staged procedures.",
  },
  {
    question: "What is the difference between liposuction and a tummy tuck?",
    answer:
      "Liposuction removes fat only. It does not remove skin and does not tighten separated abdominal muscles. A tummy tuck removes excess abdominal skin, tightens the underlying muscles, and removes fat as part of the same procedure. Patients with both excess skin and fat benefit from a tummy tuck rather than liposuction alone. Patients with localized fat and good skin elasticity are appropriate for liposuction without a tummy tuck.",
  },
  {
    question: "When will I see the final result after liposuction?",
    answer:
      "Visible improvement is present within the first 2 to 3 weeks as initial swelling reduces. The final contour is visible at approximately 3 months when all swelling has resolved. The treated areas continue to improve progressively during this period. Compression garment use during healing supports the final result.",
  },
  {
    question: "Can liposuction be combined with other procedures?",
    answer:
      "Yes, and this is common. Frequent combinations include tummy tuck for patients with both abdominal skin laxity and fat, breast procedures where fat harvested by liposuction is used for fat grafting, gynecomastia surgery where liposuction removes the fatty component alongside glandular excision, and mommy makeover packages that address multiple post-pregnancy body changes in a single surgery.",
  },
  {
    question: "What are the risks of liposuction?",
    answer:
      "Swelling, bruising, and temporary numbness in the treated areas are expected and resolve during recovery. Seroma, a collection of fluid under the skin, occurs in a small percentage of cases and is treated by drainage. Contour irregularities can occur when fat removal is uneven and may require revision. Fat embolism is a rare but serious complication. Choosing an experienced surgeon who operates within safe volume limits significantly reduces the risk of all complications.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Liposuction",
      alternateName: ["Lipoplasty", "Suction-assisted lipectomy", "Body contouring liposuction"],
      description:
        "Surgical removal of localized subcutaneous fat via tumescent technique and cannula suction; may use power-assisted, ultrasound-assisted, or laser-assisted methods; for contouring abdomen, flanks, limbs, back, submental area, and other sites when skin elasticity is adequate.",
      procedureType: "surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Body" },
      howPerformed:
        "Marking upright; tumescent infiltration; small access incisions; cannula suction in controlled passes; compression garment; local or general anesthesia; 1–4 hours; typically same-day discharge; safe total aspirate typically 2–5 liters per session.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical body contouring" },
        { "@type": "PropertyValue", name: "Not indicated for", value: "Primary weight loss or obesity treatment" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: LIPOSUCTION_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function LiposuctionJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Body", href: "/#services" },
    { name: "Liposuction", href: "/liposuction-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Body</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Liposuction Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Body Contouring &amp; Fat Removal by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Liposuction" icon="⚡" size="hero"  imageSrc={HERO.liposuction} imageAlt="Liposuction"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Liposuction Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Liposuction is a surgical procedure that removes localized fat deposits from specific areas of the body that do not
              reduce with diet and exercise. It is a body contouring procedure, not a weight loss treatment. The distinction matters.
              Liposuction works best on patients who are close to their ideal weight but have specific areas where fat distribution is
              disproportionate to the rest of their body.
            </p>
            <p>
              Fat cells in these resistant areas, typically the abdomen, flanks, thighs, arms, and back, are genetically
              predetermined in their distribution and do not respond to caloric deficit the same way fat elsewhere does. Some
              patients carry fat in the same location regardless of how lean they are overall. Liposuction removes these fat cells
              permanently. The removed cells do not return. The remaining fat cells in the treated area can still enlarge with
              significant weight gain, which is why maintaining a stable weight after surgery preserves the result.
            </p>
            <p>
              Dr. Vicky Jain performs liposuction as a standalone body contouring procedure and as part of combination
              surgeries including tummy tuck, mommy makeover, and breast fat transfer. The procedure takes 1 to 4 hours depending on
              the number of areas treated, and most patients return to light activity within a week.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Liposuction</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Liposuction produces a contour result, not just a size reduction. The difference between removing fat and sculpting a
                body is the attention to transition zones: how the treated area blends into the surrounding untreated tissue. Removing
                fat from the abdomen without accounting for how it transitions into the flanks and hips creates a result that looks
                operated on rather than naturally proportionate. Dr. Jain marks all treatment areas and their boundaries before surgery
                with the patient standing upright, assessing how each area relates to adjacent anatomy.
              </p>
              <p>
                Skin elasticity is evaluated before surgery for every patient. Liposuction removes volume but does not remove skin. In
                patients with good skin elasticity, the skin contracts after fat removal and the result is smooth. In patients with
                reduced elasticity, removing fat without addressing the skin creates laxity. For patients in this category, Dr. Jain
                advises on whether liposuction alone produces the intended result or whether a skin-removing procedure alongside it,
                such as a tummy tuck for the abdomen, is the appropriate plan.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreasTreatedSection() {
  const cards = [
    {
      title: "Abdomen",
      icon: Minimize2,
      copy: "Upper and lower abdominal fat that persists despite a stable weight and regular exercise. The most commonly treated area. Frequently combined with flank treatment for a complete waistline result.",
    },
    {
      title: "Flanks and Love Handles",
      icon: MoveHorizontal,
      copy: "Fat along the sides of the waist between the lower ribs and hip. Creates the appearance of a wider waist and blunts the waist-to-hip ratio. Often treated alongside the abdomen for a circumferential waistline improvement.",
    },
    {
      title: "Thighs",
      icon: Zap,
      copy: "Inner thigh fat that causes thighs to rub together, outer thigh bulge that widens the lower body silhouette, and anterior thigh fullness. Each area is assessed independently and treated based on where the disproportionate fat sits.",
    },
    {
      title: "Arms",
      icon: Shirt,
      copy: "Upper arm fat, particularly on the posterior surface, that creates fullness when the arms are at rest and remains resistant to exercise that builds the tricep muscle beneath. Skin elasticity assessment is important here before planning arm liposuction.",
    },
    {
      title: "Back",
      icon: Layers,
      copy: "Upper back rolls that create visible bulges beneath bra straps, lower back fat above the waist, and posterior flank fullness. Back liposuction is often performed alongside abdominal and flank treatment for complete circumferential waist contouring.",
    },
    {
      title: "Double Chin and Neck",
      icon: UserRound,
      copy: "Submental fat beneath the chin that blunts the jaw-to-neck angle. For younger patients with good skin elasticity and no platysmal banding, chin liposuction alone produces a clean jaw contour without the need for a neck lift.",
    },
    {
      title: "Male Chest (Gynecomastia)",
      icon: User,
      copy: "Fatty gynecomastia in men where the chest fullness is primarily adipose tissue rather than glandular breast tissue. Liposuction alone addresses the fatty component. When significant glandular tissue is present, direct excision is added alongside liposuction.",
    },
    {
      title: "Hips and Buttocks",
      icon: Sparkles,
      copy: "Hip fat that widens the silhouette below the waist, and buttock contouring where specific areas of fat are removed to improve shape. Fat harvested from these areas is frequently used for fat grafting to other locations when a transfer procedure is planned.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="areas-treated-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Body Areas Treated with Liposuction</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TECHNIQUES = [
  {
    title: "Tumescent Liposuction",
    body:
      "The standard and most widely used technique. A solution of saline, local anesthetic, and adrenaline is injected into the target area before fat removal. This causes the fat to swell and firm up, reduces bleeding during the procedure, and provides local anesthesia that reduces post-operative discomfort. The fat is then removed through a thin cannula. Tumescent technique is used as the foundation in all liposuction procedures regardless of whether additional energy is applied.",
  },
  {
    title: "Power-Assisted Liposuction (PAL)",
    body:
      "A motorized cannula vibrates rapidly during fat removal, breaking up fat more efficiently than manual cannula movement alone. Reduces physical effort during surgery and allows more precise fat removal in fibrous areas such as the back and previously treated sites. Produces less surgeon fatigue during long or multi-area procedures.",
  },
  {
    title: "Ultrasound-Assisted Liposuction (UAL)",
    body:
      "Ultrasound energy is delivered through a probe to liquefy fat cells before suction. Particularly effective in fibrous areas where standard liposuction is more difficult. The liquefied fat is then removed by suction. Produces some degree of skin tightening through tissue heating, though this effect is modest and not a substitute for skin removal in patients with significant laxity.",
  },
  {
    title: "Laser Liposuction",
    body:
      "Laser energy liquefies fat and provides mild stimulation to the overlying skin. The effect on skin tightening is limited and inconsistent. Best used for small, superficial areas where moderate contouring and modest skin improvement are the goals. Not appropriate as a primary technique for large-volume fat removal.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Liposuction Techniques</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TECHNIQUES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Liposuction Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1 to 4 hours under local or general anesthesia depending on the number of areas treated and the volume of
              fat being removed. Dr. Jain marks all treatment areas with the patient standing before surgery, mapping the fat
              distribution and the transition zones between treated and untreated areas.
            </p>
            <p>
              Tumescent solution is infiltrated into each treatment area. Small access incisions of 2 to 5 millimeters are made in
              positions that are concealed by natural skin folds or body contours. A cannula is passed through these incisions and fat
              is removed by suction in controlled, even passes across the treatment area. The depth and direction of the cannula passes
              determines the smoothness of the final contour.
            </p>
            <p>
              Safe fat removal per session is approximately 2 to 5 liters depending on the patient&apos;s body size and health.
              Removing beyond safe limits increases complication risk. For patients who need more volume removed, staged procedures are
              planned. A compression garment is applied immediately after surgery. Most patients go home the same day.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 5",
      description:
        "Swelling, bruising, and soreness in the treated areas. The compression garment stays on continuously. Most patients are mobile the day after surgery.",
      icon: BedDouble,
    },
    {
      period: "Week 1 to 2",
      description:
        "Most patients return to desk work by day 7 to 10. Compression garment continues. Visible improvement even through swelling.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Most normal daily activity resumes. Swelling continues to reduce. The treated areas look progressively more defined.",
      icon: Clock,
    },
    {
      period: "Month 3",
      description:
        "Final contour is visible. Swelling has fully resolved. The compression garment is no longer required. Exercise is fully resumed by week 6.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_ROWS = [
  {
    label: "What It Removes",
    lipo: "Localized fat deposits only.",
    tuck: "Fat plus excess abdominal skin and muscle laxity.",
    weight: "General body fat, not targeted.",
  },
  {
    label: "Skin Removal",
    lipo: "No. Fat is removed, skin must contract on its own.",
    tuck: "Yes. Excess skin is surgically removed.",
    weight: "No surgical skin removal.",
  },
  {
    label: "Muscle Tightening",
    lipo: "No.",
    tuck: "Yes. Separated abdominal muscles are repaired.",
    weight: "No.",
  },
  {
    label: "Best For",
    lipo: "Patients with good skin elasticity and localized fat deposits at or near ideal weight.",
    tuck: "Patients with excess abdominal skin, muscle separation, and fat who want complete abdominal correction.",
    weight: "Patients who are overweight and need overall fat reduction before body contouring.",
  },
  {
    label: "Permanence",
    lipo: "Fat cells removed permanently. Remaining cells can enlarge with weight gain.",
    tuck: "Skin and fat removal permanent. Muscle repair permanent.",
    weight: "Variable, requires sustained lifestyle maintenance.",
  },
];

function LiposuctionVsRelatedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="lipo-vs-related-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Liposuction vs. Tummy Tuck vs. Weight Loss
          </h2>
        </div>
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[720px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-3 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[14%]" />
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Liposuction
                </th>
                <th scope="col" className="py-3 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Tummy Tuck
                </th>
                <th scope="col" className="py-3 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  Weight Loss
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.lipo}</td>
                  <td className="py-3 px-2 sm:px-3 text-deep-navy/70 align-top">{row.tuck}</td>
                  <td className="py-3 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-6xl mx-auto mt-8 text-deep-navy/70 text-center leading-relaxed text-sm md:text-base">
          Liposuction and tummy tuck are frequently combined in patients who have both excess skin and localized fat. Liposuction
          alone on a patient with significant abdominal skin laxity produces a result with improved fat but worsened skin appearance.
          Correct patient selection prevents this outcome.
        </p>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Liposuction?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults at or near their ideal body weight with good skin elasticity and specific localized fat
              deposits that are disproportionate to their overall body. The closer a patient is to their stable weight before
              liposuction, the better the result. Liposuction is not a treatment for obesity. Patients who are significantly
              overweight get better outcomes from weight loss before body contouring rather than using liposuction as a weight loss
              substitute.
            </p>
            <p>
              Skin elasticity is a non-negotiable assessment factor. Young patients with tight skin get clean, smooth results from
              liposuction. Older patients, patients with significant prior weight loss, and patients who have had pregnancies may have
              reduced elasticity that makes liposuction alone insufficient. Dr. Jain assesses elasticity during consultation and
              advises on whether liposuction alone produces the intended result or whether a skin-removing procedure is needed to
              achieve it. Non-smokers or patients who stop smoking before surgery have better healing outcomes and lower complication
              rates. Patients at a stable weight for at least 3 to 6 months before surgery get the most predictable long-term result.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Liposuction" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {LIPOSUCTION_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`lipo-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Liposuction Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your fat distribution, skin elasticity, and body proportions to determine
          which areas liposuction addresses effectively and whether any concurrent procedure improves the overall result. You leave
          with a clear treatment plan, a realistic picture of the expected outcome, and a full understanding of the recovery process.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Tummy Tuck",
      description:
        "Remove excess abdominal skin and tighten muscles alongside fat removal for complete abdominal correction.",
      href: "/tummy-tuck-surgery-mumbai",
    },
    {
      name: "Breast Fat Transfer",
      description: "Use fat harvested during liposuction for natural breast enhancement in a single procedure.",
      href: "/breast-fat-transfer-lipofilling-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Combined post-pregnancy body restoration addressing multiple areas in one surgery.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LiposuctionPage() {
  return (
    <>
      <SEO
        title="Liposuction Surgery | Dr. Vicky Jain"
        description="Stubborn fat that does not respond to diet or exercise needs targeted removal. Dr. Vicky Jain performs liposuction for precise body contouring. Book today."
        canonical="/liposuction-surgery-mumbai"
      />
      <LiposuctionJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <AreasTreatedSection />
      <TechniquesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <LiposuctionVsRelatedSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
